//---------------------------------------------------------
// Redirect for paths outside the app's basename (/netlox)
//---------------------------------------------------------
// The edge serves index.html for every unknown path, so a link such as
// /soft/pairs/<id> loads the SPA outside the React Router basename and renders
// a blank page. This maps such paths back into the app.
//
// Security (open-redirect): the result is ALWAYS a fixed same-site path that
// starts with `${basename}/`. Only the route path itself is carried over, and
// only when it matches a known app route; query strings, hash, Referer and any
// absolute / protocol-relative / backslash / dot-segment input are ignored and
// fall back to the Instances page.

const FALLBACK = '/instance';

// Known /soft routes (see App.tsx). Pair ids are restricted to a safe charset.
const KNOWN_SOFT_ROUTES: RegExp[] = [
	/^\/soft\/pairs\/[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/,
	/^\/soft\/pairs$/,
	/^\/soft\/settings\/linode$/,
	/^\/soft\/templates$/,
	/^\/soft\/provision$/,
];

function isSafeRoutePath(path: string): boolean {
	if (!path.startsWith('/') || path.startsWith('//')) return false;
	if (/[\\%\s]/.test(path)) return false; // backslash, encoded or whitespace tricks
	if (path.includes('//')) return false;
	return !path.split('/').some((seg) => seg === '.' || seg === '..');
}

/**
 * Returns the in-app redirect target for `pathname`, or null when the path is
 * already inside `basename` (no redirect needed). `pathname` must be
 * `window.location.pathname` only — never search, hash or Referer.
 */
export function outsideBasenameRedirect(pathname: string, basename: string): string | null {
	const base = basename.replace(/\/+$/, '');
	if (!base) return null; // app served at root: router handles everything
	if (pathname === base || pathname.startsWith(base + '/')) return null;

	if (isSafeRoutePath(pathname)) {
		const trimmed = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
		if (KNOWN_SOFT_ROUTES.some((re) => re.test(trimmed))) return base + trimmed;
	}
	return base + FALLBACK;
}
